import { DayKey, HourData } from 'components/restaurantInformation/components/types';
import { parseTimeToMinutes } from 'helpers/orderInAdvanceHelper';
import {
    ReservationRequestDaySettings,
    ReservationRequestPeriod,
    ReservationRequestTimes,
    ReservationRequestTimesIssue,
} from 'store/restaurantDetailsStore/reducer';

export const RESERVATION_REQUEST_DAY_KEYS: DayKey[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
];

export type RequestWorkingHours = Partial<Record<DayKey, HourData[]>> | null | undefined;

type Interval = { from: number; to: number };

function getDayIntervals(hours: HourData[] | undefined): Interval[] {
    return (hours ?? []).reduce<Interval[]>((intervals, slot) => {
        const from = parseTimeToMinutes(slot.from);
        const to = parseTimeToMinutes(slot.to);
        if (from === null || to === null) return intervals;
        if (from === 0 && to === 0) {
            intervals.push({ from: 0, to: 24 * 60 });
        } else if (from <= to) {
            intervals.push({ from, to });
        }
        return intervals;
    }, []);
}

function toPickerValue(minutes: number): number {
    return Math.floor(minutes / 60) + (minutes % 60) / 100;
}

function formatTime(minutes: number): string {
    return `${Math.floor(minutes / 60)
        .toString()
        .padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}`;
}

function isValidStartTime(value: number | null): value is number {
    if (value == null) return false;
    const minutes = parseTimeToMinutes(value);
    return minutes !== null && minutes <= 23 * 60 + 30 && minutes % 30 === 0;
}

export function getDefaultReservationRequestTimes(
    workingHours: RequestWorkingHours,
    durationMinutes: number
): ReservationRequestTimes {
    return RESERVATION_REQUEST_DAY_KEYS.reduce<ReservationRequestTimes>((result, day) => {
        const intervals = getDayIntervals(workingHours?.[day]);
        if (intervals.length === 0) return result;

        const periods: ReservationRequestPeriod[] = [];
        const coveredStarts = new Set<number>();
        const orderedIntervals = intervals
            .slice()
            .sort((left, right) => left.from - right.from || right.to - left.to);
        orderedIntervals.forEach((interval) => {
            const firstStart = Math.ceil(interval.from / 30) * 30;
            const lastStart = Math.floor((interval.to - durationMinutes) / 30) * 30;
            if (
                firstStart > lastStart ||
                firstStart > 23 * 60 + 30 ||
                lastStart > 23 * 60 + 30
            ) {
                return;
            }

            let rangeFrom: number | null = null;
            let rangeTo: number | null = null;
            const addRange = () => {
                if (rangeFrom !== null && rangeTo !== null) {
                    periods.push({
                        from: toPickerValue(rangeFrom),
                        to: toPickerValue(rangeTo),
                    });
                }
                rangeFrom = null;
                rangeTo = null;
            };
            for (let start = firstStart; start <= lastStart; start += 30) {
                if (coveredStarts.has(start)) {
                    addRange();
                    continue;
                }
                coveredStarts.add(start);
                if (rangeFrom === null) {
                    rangeFrom = start;
                }
                rangeTo = start;
            }
            addRange();
        });

        result[day] = {
            isEnabled: periods.length > 0,
            periods,
        };
        return result;
    }, {});
}

export function getReservationRequestTimesIssues(
    requestTimes: ReservationRequestTimes | null | undefined,
    workingHours: RequestWorkingHours,
    durationMinutes: number
): Partial<Record<DayKey, ReservationRequestTimesIssue[]>> {
    const errors: Partial<Record<DayKey, ReservationRequestTimesIssue[]>> = {};
    RESERVATION_REQUEST_DAY_KEYS.forEach((day) => {
        const intervals = getDayIntervals(workingHours?.[day]);
        const daySettings = requestTimes?.[day];
        if (intervals.length === 0 || !daySettings?.isEnabled) return;

        const issues: ReservationRequestTimesIssue[] = [];
        const periods = daySettings.periods ?? [];
        if (periods.length === 0) {
            issues.push({ key: 'addPeriodOrSwitchOff' });
        }

        const validPeriods = periods
            .filter(
                (period): period is ReservationRequestPeriod =>
                    period.from !== null && period.from !== undefined &&
                    period.to !== null && period.to !== undefined
            )
            .slice()
            .sort(
                (left, right) =>
                    (parseTimeToMinutes(left.from) ?? 0) -
                    (parseTimeToMinutes(right.from) ?? 0)
            );
        if (validPeriods.length !== periods.length) {
            issues.push({ key: 'completePeriodRequired' });
        }

        validPeriods.forEach((period, index) => {
            if (!isValidStartTime(period.from) || !isValidStartTime(period.to)) {
                issues.push({ key: 'requestTimesStepError' });
                return;
            }

            const from = parseTimeToMinutes(period.from)!;
            const to = parseTimeToMinutes(period.to)!;
            if (to < from) {
                issues.push({ key: 'lastStartBeforeFirst' });
                return;
            }

            if (index > 0) {
                const overlappingPeriod = validPeriods
                    .slice(0, index)
                    .find((previous) => {
                        const previousEnd = parseTimeToMinutes(previous.to!);
                        return previousEnd !== null && previousEnd >= from;
                    });
                if (overlappingPeriod) {
                    issues.push({
                        key: 'periodsOverlap',
                        params: {
                            first: formatTime(parseTimeToMinutes(overlappingPeriod.from!)!),
                            second: formatTime(from),
                        },
                    });
                }
            }

            const interval = intervals
                .filter((candidate) => from >= candidate.from && to <= candidate.to)
                .sort((left, right) => right.to - left.to)[0];
            if (!interval) {
                issues.push({
                    key: 'outsideDineIn',
                    params: {
                        hours: intervals
                            .map((item) => `${formatTime(item.from)}–${formatTime(item.to)}`)
                            .join(', '),
                    },
                });
                return;
            }

            const firstPossibleStart = Math.ceil(interval.from / 30) * 30;
            const latestPossibleStart =
                Math.floor((interval.to - durationMinutes) / 30) * 30;
            if (firstPossibleStart > latestPossibleStart) {
                issues.push({
                    key: 'shortPeriod',
                    params: {
                        period: `${formatTime(from)}–${formatTime(to)}`,
                        duration: formatDuration(durationMinutes),
                    },
                });
            } else if (from > latestPossibleStart || to > latestPossibleStart) {
                issues.push({
                    key: 'lastStartTooLate',
                    params: {
                        time: formatTime(latestPossibleStart),
                        closing: formatTime(interval.to),
                        duration: formatDuration(durationMinutes),
                    },
                });
            }
        });

        if (issues.length > 0) errors[day] = issues;
    });

    return errors;
}

export function getAvailableReservationStarts(
    day: DayKey,
    requestTimes: ReservationRequestTimes | null | undefined,
    followsWorkingHours: boolean,
    workingHours: RequestWorkingHours,
    durationMinutes: number
): number[] {
    const intervals = getDayIntervals(workingHours?.[day]);
    if (intervals.length === 0) return [];

    const settings = requestTimes?.[day];
    if (!followsWorkingHours && !settings?.isEnabled) return [];

    const configuredPeriods = followsWorkingHours
        ? intervals.map((interval) => ({
              from: toPickerValue(Math.ceil(interval.from / 30) * 30),
              to: toPickerValue(Math.floor((interval.to - durationMinutes) / 30) * 30),
          }))
        : settings?.periods ?? [];

    const starts = new Set<number>();
    configuredPeriods.forEach((period) => {
        if (!isValidStartTime(period.from) || !isValidStartTime(period.to)) return;
        const from = parseTimeToMinutes(period.from)!;
        const to = parseTimeToMinutes(period.to)!;
        if (to < from) return;
        for (let start = from; start <= to; start += 30) {
            if (
                intervals.some(
                    (interval) =>
                        start >= interval.from && start + durationMinutes <= interval.to
                )
            ) {
                starts.add(start);
            }
        }
    });
    return Array.from(starts).sort((left, right) => left - right);
}

function formatDuration(minutes: number): string {
    if (minutes % 60 === 0) return `${minutes / 60} h`;
    return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

export function formatReservationStarts(starts: number[]): string {
    if (starts.length === 0) return '';
    const ranges: Array<[number, number]> = [];
    starts.forEach((start) => {
        const previous = ranges[ranges.length - 1];
        if (previous && start === previous[1] + 30) {
            previous[1] = start;
        } else {
            ranges.push([start, start]);
        }
    });
    return ranges
        .flatMap(([from, to]) => {
            if (from === to) return [formatTime(from)];
            if (to - from === 30) return [formatTime(from), formatTime(to)];
            return [`${formatTime(from)} – ${formatTime(to)}`];
        })
        .join(' · ');
}

export function hasWorkingHours(workingHours: RequestWorkingHours, day: DayKey): boolean {
    return getDayIntervals(workingHours?.[day]).length > 0;
}

export function getDayWorkingHoursLabel(workingHours: RequestWorkingHours, day: DayKey): string {
    return getDayIntervals(workingHours?.[day])
        .map((interval) => `${formatTime(interval.from)}–${formatTime(interval.to)}`)
        .join(', ');
}

export function createEmptyReservationRequestDay(): ReservationRequestDaySettings {
    return { isEnabled: true, periods: [{ from: null, to: null }] };
}
